import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Target, Palette, FileText, ChevronDown, ChevronUp, Layout, Type, Move, AlignLeft, Shield } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { simplifyRecommendation } from '../utils/languageSimplifier';

const sourceConfig = {
  strategic: {
    label: 'Strategic',
    icon: Target,
    color: 'text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400',
    badgeColor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800'
  },
  design: {
    label: 'Design',
    icon: Palette,
    color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400',
    badgeColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800'
  },
  content: {
    label: 'Content',
    icon: FileText,
    color: 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400',
    badgeColor: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800'
  },
  brand: {
    label: 'Brand',
    icon: Shield,
    color: 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400',
    badgeColor: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800'
  }
};

// Category configuration with icons and colors
const categoryConfig = {
  spacing: { label: 'Spacing & Margins', icon: Move, color: 'text-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-900/20', borderColor: 'border-blue-200 dark:border-blue-800' },
  typography: { label: 'Typography & Text', icon: Type, color: 'text-purple-600', bgColor: 'bg-purple-50 dark:bg-purple-900/20', borderColor: 'border-purple-200 dark:border-purple-800' },
  layout: { label: 'Layout & Structure', icon: Layout, color: 'text-orange-600', bgColor: 'bg-orange-50 dark:bg-orange-900/20', borderColor: 'border-orange-200 dark:border-orange-800' },
  content: { label: 'Content & Messaging', icon: AlignLeft, color: 'text-green-600', bgColor: 'bg-green-50 dark:bg-green-900/20', borderColor: 'border-green-200 dark:border-green-800' },
  brand: { label: 'Brand Guidelines', icon: Shield, color: 'text-indigo-600', bgColor: 'bg-indigo-50 dark:bg-indigo-900/20', borderColor: 'border-indigo-200 dark:border-indigo-800' },
  visual: { label: 'Visual Design', icon: Palette, color: 'text-pink-600', bgColor: 'bg-pink-50 dark:bg-pink-900/20', borderColor: 'border-pink-200 dark:border-pink-800' },
  other: { label: 'Other Issues', icon: Target, color: 'text-gray-600', bgColor: 'bg-gray-50 dark:bg-gray-900/20', borderColor: 'border-gray-200 dark:border-gray-800' }
};

function ChecklistItem({ item, onCheck, expertiseLevel }) {
  const source = sourceConfig[item.source] || sourceConfig.strategic;
  const SourceIcon = source.icon;

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-border/40 bg-card/40 hover:bg-card/60 transition-all group">
      <button
        onClick={() => onCheck(item.id)}
        className="flex-shrink-0 mt-1 focus:outline-none focus:ring-2 focus:ring-primary rounded"
      >
        {item.checked ? (
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
        ) : (
          <Circle className="w-5 h-5 text-gray-400 dark:text-gray-600 group-hover:text-gray-600 dark:group-hover:text-gray-400" />
        )}
      </button>

      <div className="flex-1 min-w-0 space-y-2">
        <p className={`text-sm leading-relaxed ${item.checked ? 'text-muted-foreground line-through' : 'text-foreground font-medium'}`}>
          {simplifyRecommendation(item.text, expertiseLevel)}
        </p>

        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={source.badgeColor + ' text-xs'}>
            <SourceIcon className="w-3 h-3 mr-1" />
            {source.label}
          </Badge>

          {item.slide_number && (
            <Badge variant="outline" className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700 text-xs">
              Slide {item.slide_number}
            </Badge>
          )}

          <span className="text-xs text-muted-foreground ml-auto">
            Impact: {item.impact_score}
          </span>
        </div>
      </div>
    </div>
  );
}

function CategorySection({ category, items, onCheck, defaultExpanded, expertiseLevel, rank }) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const config = categoryConfig[category] || categoryConfig.other;
  const CategoryIcon = config.icon;

  const uncheckedCount = items.filter(item => !item.checked).length;
  const totalCount = items.length;
  const completionPercent = totalCount > 0 ? Math.round(((totalCount - uncheckedCount) / totalCount) * 100) : 0;

  if (totalCount === 0) return null;

  return (
    <div className="space-y-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between p-4 rounded-lg border ${config.borderColor} ${config.bgColor} hover:shadow-sm transition-all`}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white dark:bg-gray-800 border-2 border-current font-bold text-xs">
            {rank}
          </div>
          <CategoryIcon className={`w-5 h-5 ${config.color}`} />
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {config.label}
            </h3>
            <p className="text-xs text-muted-foreground">
              {uncheckedCount} of {totalCount} remaining
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs font-medium text-muted-foreground">
              {completionPercent}% done
            </div>
            <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-1">
              <div
                className="h-full bg-green-500 dark:bg-green-600 transition-all duration-300"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="space-y-2 pl-4">
          {items.map((item) => (
            <ChecklistItem key={item.id} item={item} onCheck={onCheck} expertiseLevel={expertiseLevel} />
          ))}
        </div>
      )}
    </div>
  );
}

export function UnifiedPriorityList({ analysis, unifiedAnalysis, expertiseLevel = 'beginner', projectId }) {
  // Support both props for backward compatibility
  const unified = unifiedAnalysis || analysis?.unified_analysis;

  // Track checked items in state with localStorage persistence
  const storageKey = `checklist-${projectId || 'default'}`;
  const [checkedItems, setCheckedItems] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Save to localStorage whenever checkedItems changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify([...checkedItems]));
    } catch (error) {
      console.error('Failed to save checklist state:', error);
    }
  }, [checkedItems, storageKey]);

  if (!unified || !unified.unified_priorities) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>The Checklist</CardTitle>
          <CardDescription>No priority data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const priorities = unified.unified_priorities || [];

  // Categorize items by type
  const categorizeItem = (item) => {
    const text = item.text.toLowerCase();
    const category = item.category?.toLowerCase() || '';

    // Check explicit category first (more reliable than text matching)
    if (category.includes('content')) {
      return 'content';
    } else if (category.includes('brand')) {
      return 'brand';
    } else if (category.includes('spacing') || category.includes('margin')) {
      return 'spacing';
    } else if (category.includes('typography')) {
      return 'typography';
    } else if (category.includes('layout')) {
      return 'layout';
    } else if (category.includes('visual')) {
      return 'visual';
    }

    // Fallback to text matching only if no explicit category
    if (text.includes('margin') || text.includes('spacing') || text.includes('space') || text.includes('padding')) {
      return 'spacing';
    } else if (text.includes('font') || text.includes('text') || text.includes('typography') || text.includes('hierarchy')) {
      return 'typography';
    } else if (text.includes('layout') || text.includes('structure') || text.includes('alignment') || text.includes('grid')) {
      return 'layout';
    } else if (text.includes('brand') || text.includes('guideline') || text.includes('compliance') || item.source === 'brand') {
      return 'brand';
    } else if (text.includes('content') || text.includes('message') || text.includes('narrative') || text.includes('story')) {
      return 'content';
    } else if (text.includes('color') || text.includes('visual') || text.includes('image') || text.includes('graphic')) {
      return 'visual';
    }
    return 'other';
  };

  // Add IDs and checked status to items
  const itemsWithStatus = priorities.map((p, idx) => ({
    ...p,
    id: `item-${idx}`,
    checked: checkedItems.has(`item-${idx}`),
    category: categorizeItem(p)
  }));

  // Group by category
  const itemsByCategory = itemsWithStatus.reduce((acc, item) => {
    const cat = item.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Calculate category importance (based on impact scores and count)
  const categoryImportance = Object.entries(itemsByCategory).map(([category, items]) => {
    const avgImpact = items.reduce((sum, item) => sum + (item.impact_score || 5), 0) / items.length;
    const count = items.length;
    const highPriorityCount = items.filter(item => item.priority_level === 'high').length;

    // Importance score: weighted by average impact, count, and high priority items
    const importance = (avgImpact * 0.4) + (count * 0.3) + (highPriorityCount * 3 * 0.3);

    return { category, items, importance };
  }).sort((a, b) => b.importance - a.importance);

  const handleCheck = (itemId) => {
    setCheckedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const totalItems = itemsWithStatus.length;
  const checkedCount = checkedItems.size;
  const overallProgress = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  return (
    <Card className="border-border/40 bg-card/60">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-xl font-bold text-foreground">The Checklist</CardTitle>
            <CardDescription className="mt-1">
              Issues organized by type, ranked by importance
            </CardDescription>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold text-foreground">{checkedCount} / {totalItems}</div>
            <div className="text-xs text-muted-foreground">Completed</div>
          </div>
        </div>

        {/* Overall Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Overall Progress</span>
            <span className="text-sm font-bold text-foreground">{overallProgress}%</span>
          </div>
          <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 transition-all duration-300"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {categoryImportance.map(({ category, items }, index) => (
          <CategorySection
            key={category}
            category={category}
            items={items}
            onCheck={handleCheck}
            defaultExpanded={index === 0}
            expertiseLevel={expertiseLevel}
            rank={index + 1}
          />
        ))}

        {totalItems === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50 text-green-500" />
            <p className="font-medium">Great work! No issues identified.</p>
            <p className="text-sm mt-1">Your presentation looks good.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
